import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { DetailRow } from './DetailRow';

export type DetailRowView = {
  label: ReactNode;
  text: ReactNode;
};

/**
 * Desktop grid template used by the "columns" layout. Matches the
 * ServiceListAccordion look — label column takes 1fr, arrow auto, text 2fr.
 * Callers can override with `columnsClassName` when the right-hand column
 * should be narrow (e.g. LabeledStatements shrinks the label column to
 * `max-content` so it hugs its longest row).
 */
const DEFAULT_COLUMNS_CLASS_NAME =
  'md:grid-cols-[minmax(0,1fr)_auto_minmax(0,2fr)]';

type Props = {
  intro?: string;
  leftColumnTitle: string;
  rightColumnTitle: string;
  rows: DetailRowView[];
  layout?: 'columns' | 'list';
  heading?: string;
  /**
   * Row arrow colour. Defaults match the ServiceListAccordion look
   * (`#000000` for columns, `#7c5cff` for list); pass a custom value only
   * when the surrounding slice needs a different accent.
   */
  arrowColor?: string;
  /**
   * Tailwind override for the desktop 3-column grid template. Defaults to
   * `md:grid-cols-[minmax(0,1fr)_auto_minmax(0,2fr)]`. Only applied when
   * `layout` is `"columns"`.
   */
  columnsClassName?: string;
  /**
   * Typography override for the label cell in each row. See DetailRow —
   * defaults to the accordion look, callers with rich-text labels usually
   * drop `font-semibold` so `<strong>` marks in the field win.
   */
  labelClassName?: string;
  /**
   * Typography override for the text cell in each row (columns layout).
   * Forwarded to DetailRow; defaults to the accordion look, callers pass this
   * to swap the text colour without forking the shared row.
   */
  textClassName?: string;
  /**
   * Tailwind class controlling the gap between the header row (column titles)
   * and the first data row on desktop. Defaults to `pb-[36px]` to match the
   * ServiceListAccordion look — pass a smaller value (e.g. `pb-[26px]`) when
   * the surrounding slice wants the header sitting tighter above the rows.
   */
  headerBottomSpacing?: string;
};

export { DetailRow };

/**
 * Two-column comparison panel used by ServiceListAccordion (inside its
 * expandable cards) and by the differentiator variant of
 * StackedHeaderStatementBoxes. On desktop it renders a 3-column grid
 * (label | arrow | text) with an optional header row; on mobile the rows
 * stack with eyebrow-style column labels so the "which column is this"
 * cue survives the collapse.
 */
export function DetailComparisonPanel({
  intro = '',
  leftColumnTitle,
  rightColumnTitle,
  rows,
  layout = 'columns',
  heading = '',
  arrowColor,
  columnsClassName = DEFAULT_COLUMNS_CLASS_NAME,
  labelClassName,
  textClassName,
  headerBottomSpacing = 'pb-[36px]',
}: Props) {
  if (layout === 'list') {
    const listRows = rows.filter((row) => row.text);
    if (!heading && listRows.length === 0) {
      return null;
    }

    return (
      <div className="flex w-full flex-col rounded-[10px] bg-white px-6 py-6 md:px-8 md:py-8">
        {heading ? (
          <p className="text-[20px] font-semibold leading-[1.4] text-black md:text-[24px]">
            {heading}
          </p>
        ) : null}
        {listRows.length > 0 ? (
          <div className={heading ? 'mt-4' : undefined}>
            {listRows.map((row, index) => (
              <DetailRow
                key={index}
                label=""
                text={row.text}
                isLast={index === listRows.length - 1}
                layout="list"
                arrowColor={arrowColor}
              />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  const showHeader = Boolean(leftColumnTitle || rightColumnTitle);
  const visibleRows = rows.filter((row) => row.label || row.text);

  if (!intro && !showHeader && visibleRows.length === 0) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-[44px]">
      {intro ? (
        <p className="w-full text-[16px] font-light leading-[1.5] text-black md:text-[18px]">
          {intro}
        </p>
      ) : null}

      {showHeader || visibleRows.length > 0 ? (
        <div
          className={cn(
            'grid w-full grid-cols-1 items-stretch px-[24px] md:gap-x-[2.5rem]',
            columnsClassName,
          )}
        >
          {showHeader ? (
            <>
              <p
                className={cn(
                  'hidden text-[24px] font-semibold leading-[1.4] text-black md:block',
                  headerBottomSpacing,
                )}
              >
                {leftColumnTitle}
              </p>
              <span
                aria-hidden
                className={cn('hidden md:block', headerBottomSpacing)}
              />
              <p
                className={cn(
                  'hidden text-[24px] font-semibold leading-[1.4] text-black md:block',
                  headerBottomSpacing,
                )}
              >
                {rightColumnTitle}
              </p>
            </>
          ) : null}

          {visibleRows.map((row, index) => (
            <DetailRow
              key={index}
              label={row.label}
              text={row.text}
              isLast={index === visibleRows.length - 1}
              leftColumnTitle={leftColumnTitle}
              rightColumnTitle={rightColumnTitle}
              arrowColor={arrowColor}
              labelClassName={labelClassName}
              textClassName={textClassName}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}
